import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Download, Headphones } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import LibraryCard from '../components/LibraryCard';
import MiniAudioPlayer from '../components/MiniAudioPlayer';
import FullArticleView from '../components/FullArticleView';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { getDownloadedArticles, removeArticleFromLibrary, clearAllDownloads, getLibraryStats } from '../utils/audioLibrary';
import { useTheme } from '../context/ThemeContext';
import { App as CapacitorApp } from '@capacitor/app';

const AudioLibrary = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [articles, setArticles] = useState([]);
  const [audioPlayer, setAudioPlayer] = useState({
    isOpen: false,
    article: null,
    type: null,
    isPlaying: false,
  });
  const [fullArticleView, setFullArticleView] = useState({
    isOpen: false,
    article: null,
  });
  const [stats, setStats] = useState({ total: 0, categories: [], sources: [] });
  const { toast } = useToast();

  // Handle Android back button
  useEffect(() => {
    let backButtonListener;
    
    const setupBackButton = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', () => {
        // If full article view is open, close it
        if (fullArticleView.isOpen) {
          setFullArticleView({ isOpen: false, article: null });
          return;
        }
        
        // If audio player is open, stop and close it
        if (audioPlayer.isOpen) {
          setAudioPlayer({ isOpen: false, article: null, type: null, isPlaying: false });
          return;
        }
        
        // Otherwise go back to home
        navigate('/');
      });
    };
    
    setupBackButton();

    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, [fullArticleView.isOpen, audioPlayer.isOpen, navigate]);

  // Load downloaded articles
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = () => {
    const downloaded = getDownloadedArticles();
    setArticles(downloaded);
    setStats(getLibraryStats());
  };

  const handlePlaySummary = (article) => {
    if (audioPlayer.article?.id === article.id && audioPlayer.type === 'summary' && audioPlayer.isPlaying) {
      setAudioPlayer(prev => ({ ...prev, isPlaying: false }));
    } else {
      setAudioPlayer({
        isOpen: true,
        article,
        type: 'summary',
        isPlaying: true,
      });
    }
  };

  const handlePlayFull = (article) => {
    if (audioPlayer.article?.id === article.id && audioPlayer.type === 'full' && audioPlayer.isPlaying) {
      setAudioPlayer(prev => ({ ...prev, isPlaying: false }));
    } else {
      setAudioPlayer({
        isOpen: true,
        article,
        type: 'full',
        isPlaying: true,
      });
    }
  };

  const handleTextRead = (article) => {
    setFullArticleView({
      isOpen: true,
      article,
    });
  };

  const handleDelete = (article) => {
    const result = removeArticleFromLibrary(article.id);
    if (result.success) {
      toast({
        title: 'Article Deleted',
        description: 'Article removed from your library',
      });
      loadArticles(); // Refresh the list
      
      // Close audio player if deleted article is playing
      if (audioPlayer.article?.id === article.id) {
        setAudioPlayer({ isOpen: false, article: null, type: null, isPlaying: false });
      }
    } else {
      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  const handleClearAll = () => {
    if (articles.length === 0) {
      toast({
        title: 'No Articles',
        description: 'Your library is already empty',
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete all downloaded articles?')) {
      const result = clearAllDownloads();
      if (result.success) {
        toast({
          title: 'Library Cleared',
          description: 'All articles have been deleted',
        });
        loadArticles();
        setAudioPlayer({ isOpen: false, article: null, type: null, isPlaying: false });
      }
    }
  };

  const handleShare = (article) => {
    toast({
      title: 'Share',
      description: 'Opening share options...',
    });
  };

  const handleCloseAudioPlayer = () => {
    setAudioPlayer({ isOpen: false, article: null, type: null, isPlaying: false });
  };

  const handleAudioPlayerStateChange = useCallback((isPlaying) => {
    setAudioPlayer(prev => ({ ...prev, isPlaying }));
  }, []);

  const handleCloseFullArticle = () => {
    setFullArticleView({ isOpen: false, article: null });
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Safe area padding for mobile status bar */}
        <div className="pt-[env(safe-area-inset-top,12px)]" />
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Headphones className={`w-6 h-6 ${darkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Audio Library</h1>
            </div>
            {articles.length > 0 && (
              <Button
                onClick={handleClearAll}
                variant="outline"
                size="sm"
                className={`${darkMode ? 'border-red-500 text-red-400 hover:bg-red-900/30' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
          
          {/* Stats */}
          {articles.length > 0 && (
            <div className={`flex items-center gap-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <span className="flex items-center gap-1">
                <Download className="w-4 h-4" />
                {stats.total} article{stats.total !== 1 ? 's' : ''}
              </span>
              <span>•</span>
              <span>{stats.categories.length} categories</span>
              <span>•</span>
              <span>{stats.sources.length} sources</span>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className={`px-4 py-4 ${audioPlayer.isOpen ? 'pb-32' : 'pb-20'}`}>
        {articles.length > 0 ? (
          articles.map((article) => (
            <LibraryCard
              key={article.id}
              article={article}
              onPlaySummary={handlePlaySummary}
              onPlayFull={handlePlayFull}
              onTextRead={handleTextRead}
              onDelete={handleDelete}
              onShare={handleShare}
              isPlayingSummary={audioPlayer.article?.id === article.id && audioPlayer.type === 'summary' && audioPlayer.isPlaying}
              isPlayingFull={audioPlayer.article?.id === article.id && audioPlayer.type === 'full' && audioPlayer.isPlaying}
              darkMode={darkMode}
            />
          ))
        ) : (
          <div className="text-center py-20">
            <Download className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No Downloaded Articles</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Download articles from the home page to listen offline
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-blue-700 hover:bg-blue-800"
            >
              Go to Home
            </Button>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNav activeTab="library" darkMode={darkMode} />

      {/* Mini Audio Player */}
      {audioPlayer.isOpen && (
        <MiniAudioPlayer
          article={audioPlayer.article}
          audioType={audioPlayer.type}
          onClose={handleCloseAudioPlayer}
          initialIsPlaying={audioPlayer.isPlaying}
          onPlayingStateChange={handleAudioPlayerStateChange}
          darkMode={darkMode}
        />
      )}

      {/* Full Article View */}
      {fullArticleView.isOpen && (
        <FullArticleView
          article={fullArticleView.article}
          onClose={handleCloseFullArticle}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

export default AudioLibrary;